import { useEffect, useMemo } from 'react';

import { useSetBreadcrumbTitle } from '@/hooks/useBreadcrumb';
import useFetchCandidateMailSetting from '../api/useFetchCandidateMailSetting';
import useFetchCandidateMails from '../api/useFetchCandidateMails';
import useFetchCandidateMailTemplates from '../api/useFetchCandidateMailTemplates';
import CandidateMailDetail from '../components/CandidateMailDetail';
import CandidateMailList from '../components/CandidateMailList';
import CandidateMailSettings from '../components/CandidateMailSettings';
import CandidateMailTemplateDetail from '../components/CandidateMailTemplateDetail';
import CandidateMailTemplateList from '../components/CandidateMailTemplateList';
import MailNav from '../components/MailNav';
import type { CandidateMailFolder } from '../constants';
import useCandidateMailNavigation from '../hooks/useCandidateMailNavigation';
import { mailFolderOf } from '../lib/mailFolders';
import { isMailSenderConfigured } from '../lib/mailSettings';
import { parseServerDateTime } from '../lib/scheduledAt';

const legacyHashFolders: Record<string, CandidateMailFolder> = {
  '#sent': 'sent',
  '#templates': 'templates',
  '#settings': 'settings',
};

type CandidateMailsTemplateProps = {
  folder: CandidateMailFolder;
  mailId?: number;
  templateId?: number;
};

export default function CandidateMailsTemplate({
  folder,
  mailId,
  templateId,
}: CandidateMailsTemplateProps) {
  useSetBreadcrumbTitle('メール管理');
  const navigation = useCandidateMailNavigation();
  const { data: mailSettingData } = useFetchCandidateMailSetting();
  const {
    data: mails = [],
    isLoading: isLoadingMails,
    isError: isMailsError,
    refetch: refetchMails,
  } = useFetchCandidateMails();
  const {
    data: templates = [],
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useFetchCandidateMailTemplates();

  const mailSettings = mailSettingData?.mail_settings ?? null;
  const isSenderConfigured =
    mailSettingData === undefined ? null : isMailSenderConfigured(mailSettings);

  useEffect(() => {
    const { hash } = window.location;
    if (hash === '#create') {
      void navigation.compose({ replace: true });
    } else if (legacyHashFolders[hash]) {
      void navigation.openFolder(legacyHashFolders[hash], { replace: true });
    }
  }, [navigation]);

  const mailsByFolder = useMemo(() => {
    const grouped = { sent: [], scheduled: [], archive: [] } as Record<
      ReturnType<typeof mailFolderOf>,
      typeof mails
    >;
    for (const mail of mails) grouped[mailFolderOf(mail)].push(mail);
    grouped.scheduled.sort(
      (a, b) =>
        (parseServerDateTime(a.scheduled_at)?.getTime() ?? 0) -
        (parseServerDateTime(b.scheduled_at)?.getTime() ?? 0)
    );
    return grouped;
  }, [mails]);

  const counts = {
    scheduled: isLoadingMails ? undefined : mailsByFolder.scheduled.length,
    archive: isLoadingMails ? undefined : mailsByFolder.archive.length,
    templates: isLoadingTemplates ? undefined : templates.length,
  };

  const renderPanel = () => {
    if (folder === 'settings') return <CandidateMailSettings />;

    if (folder === 'templates') {
      if (templateId !== undefined) {
        return (
          <CandidateMailTemplateDetail
            key={templateId}
            template={templates.find((template) => template.id === templateId)}
            isLoading={isLoadingTemplates}
            onBack={() => void navigation.openFolder('templates')}
            onEdit={(id) => void navigation.editTemplate(id)}
            onDuplicate={(id) => void navigation.createTemplate(id)}
          />
        );
      }
      return (
        <CandidateMailTemplateList
          templates={templates}
          isLoading={isLoadingTemplates}
          isError={isTemplatesError}
          onCreate={() => void navigation.createTemplate()}
          onOpenTemplate={(id) => void navigation.openTemplate(id)}
        />
      );
    }

    if (mailId !== undefined) {
      return (
        <CandidateMailDetail
          key={mailId}
          mailId={mailId}
          listRecord={mails.find((mail) => mail.id === mailId)}
          mailSettings={mailSettings}
          onBack={() => void navigation.openFolder(folder)}
        />
      );
    }

    return (
      <CandidateMailList
        key={folder}
        folder={folder}
        mails={mailsByFolder[folder]}
        isLoading={isLoadingMails}
        isError={isMailsError}
        onRetry={() => void refetchMails()}
        isSenderConfigured={isSenderConfigured}
        onOpenMail={(id) => void navigation.openMail(folder, id)}
        onCompose={() => void navigation.compose()}
        onOpenSettings={() => void navigation.openFolder('settings')}
      />
    );
  };

  return (
    <div className="flex min-h-[calc(100vh-60px)] w-full items-stretch gap-md bg-gray p-lg">
      <MailNav
        activeFolder={folder}
        counts={counts}
        isComposeDisabled={isSenderConfigured !== true}
        onCompose={() => void navigation.compose()}
        onSelectFolder={(nextFolder) => void navigation.openFolder(nextFolder)}
      />
      <section className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-sm bg-white">
        {renderPanel()}
      </section>
    </div>
  );
}
